import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useIntl } from 'react-intl';

import { postComment, postManyComments, updatePostStatus } from '../Services/Api';
import queriesConfig from '../Services/queriesConfig';
import AnalyticsService from '../Services/AnalyticsService';
import { generateErrorsForWeb, extractErrorForToaster } from '../Services/ApiErrorHelpers';
import { PostStatuses } from '../Utils/Types';
import { useNotifications } from '@toolpad/core/useNotifications';
import { notify } from '../Utils/NotificationUtils';

export default function useCommentAndStatusUpdate(
  posts: any[] = [],
  content: any,
  status: any,
  originalPostId: any,
  onSubmit: any,
) {
  const [errors, setErrors] = useState({});
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const intl = useIntl();

  const postsCount = posts.length;
  // we take the first post, and suppose they all have the same type
  const firstPost = posts[0] || {};
  const { type: postType, id: postId } = firstPost;

  let endpoint = postComment(postId);

  if (postsCount > 1) {
    endpoint = postManyComments;
  } else if (!content) {
    endpoint = updatePostStatus(firstPost.id);
  }

  const { mutate, isLoading } = useMutation(endpoint);

  const mutatePosts = () => {
    const isStatusUpdated =
      (status && (postsCount > 1 || status !== firstPost.status)) ||
      originalPostId !== firstPost.original_post_id;
    const newStatus = isStatusUpdated ? status : undefined;

    if (!content && !isStatusUpdated) {
      setErrors({
        ...errors,
        content: intl.formatMessage({ id: 'COMMENTS.CREATION.ERROR' }),
        status: intl.formatMessage({ id: 'COMMENTS.CREATION.ERROR' }),
      });
      notify(notifications, intl.formatMessage({ id: 'COMMENTS.CREATION.ERROR' }), 'error');

      return;
    }

    if (newStatus === PostStatuses.DUPLICATE && !originalPostId) {
      setErrors({
        ...errors,
        originalPostId: intl.formatMessage({ id: 'COMMENTS.CREATION.ERROR' }),
      });
      notify(notifications, intl.formatMessage({ id: 'COMMENTS.CREATION.ERROR' }), 'error');

      return;
    }

    return mutate(
      {
        filter: { post_record_id: posts.map((post) => post.id) },
        content,
        ...(isStatusUpdated ? { status } : {}),
        original_post_id: originalPostId,
      },
      {
        onSuccess: () => {
          if (isStatusUpdated) {
            AnalyticsService.track('Post', 'Change Status', `Changed to '${status}' status`, {
              'Post ID': firstPost.id,
              'Post Type': postType,
              Bulk: postsCount > 0,
              'Original Post ID': originalPostId,
            });
          } else if (content) {
            AnalyticsService.track('Post', 'Comment', 'Create comment on post', {
              'Post ID': firstPost.id,
              'Post Type': postType,
              'Post Title': firstPost.title,
              Bulk: postsCount > 0,
            });
          }

          if (postsCount === 1) {
            queryClient.invalidateQueries(queriesConfig.getPost(firstPost.id).queryKey);
            queryClient.invalidateQueries('dashboard-posts');
            queryClient.invalidateQueries(['comments', firstPost.id, 0]);

            if (isStatusUpdated) {
              notify(
                notifications,
                intl.formatMessage({ id: `COMMENTS.CREATION.SUCCESS_${postType.toUpperCase()}` }),
              );
            } else {
              notify(
                notifications,
                intl.formatMessage({ id: 'POSTS.STATUS.UPDATED_SUCCESSFULLY' }),
              );
            }
          }

          onSubmit();
        },
        onError: (response: any) => {
          setErrors(generateErrorsForWeb(response));

          notify(notifications, extractErrorForToaster(response, intl), 'error');
        },
      },
    );
  };

  const resetErrors = () => {
    setErrors({});
  };

  return { mutate: mutatePosts, errors, resetErrors, isLoading };
}
